import { Box, Paper, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { powerfulDateTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import TitleTypography, { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringContext,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";
import ButtonAddressTextField from "../ButtonAddressTextField";
import EventTvlTable from "./EventTvlTable";

const getOption = (setId) => ({
  chart: {
    height: 400,
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: true,
    },
    gridLineWidth: 1,
  },
  ...powerfulDateTooltip(
    (point) => `${point.series.name}(${point.key})`,
    (point) => `$${numberWithCommas(toFixed(point.y, 2))}`
  ),
  plotOptions: {
    series: {
      cursor: "pointer",
      events: {
        click: function (event) {
          const id = toFixed(event.point.id);
          setId(id);
        },
      },
    },
  },
});

const dateOption = () => {
  var currentTime = Math.floor(Date.now() / 1000);
  return [
    { label: "1D", from: currentTime - 86400, to: currentTime, id: "1D" },
    { label: "1W", from: currentTime - 604800, to: currentTime, id: "1W" },
    { label: "1M", from: currentTime - 2592000, to: currentTime, id: "1M" },
    { label: "3M", from: currentTime - 7776000, to: currentTime, id: "3M" },
  ];
};

export default function TotalValueLocked() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const [chartId, setChartId] = useState(null);
  const { role } = useUserContext();
  const { fetchTotalValueLocked } = useUserMonitoringSelector((state) => state.fetch);
  const { selectedAddress, setSelectedAddress, totalValueLockedLoading } = useUserMonitoringContext();
  const totalValueLocked = useUserMonitoringChartSelector(MONITORING.USER.totalValueLocked, pool);

  useEffect(() => {
    if (totalValueLocked?.active === undefined) {
      var currentTime = Math.floor(Date.now() / 1000);
      fetchTotalValueLocked(pool, { from: currentTime - 2592000, to: currentTime }, { active: 2 });
    } else fetchTotalValueLocked(pool, totalValueLocked?.metadata, { active: totalValueLocked?.active });
  }, [totalValueLocked?.active, pool, fetchTotalValueLocked]);

  const { series, check } = useMemo(() => {
    const checkData = totalValueLocked?.tvl?.length > 0;
    if (totalValueLocked && checkData && !totalValueLockedLoading) {
      return {
        check: checkData,
        series: [
          {
            name: "Total Value Lock",
            data: [].concat(totalValueLocked.tvl),
            zoneAxis: "x",
            zones: [].concat(totalValueLocked.zones),
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 6,
            },
          },
        ],
      };
    }
    return {
      check: checkData,
      series: [{ name: "Tvl", data: [] }],
    };
  }, [totalValueLocked, totalValueLockedLoading]);

  const eventData = useMemo(() => {
    if (chartId) return totalValueLocked?.transactionData[chartId];
    return null;
  }, [chartId, totalValueLocked]);

  return (
    <DetailMonitoringWrapper
      isAuthorization={true}
      navigation={{ text: "user monitoring", name: "total value locked", link: `/user-monitoring/${pool}` }}
      selectLink="/user-monitoring/total-value-locked"
      Component={{
        Description: (
          <NoteTypography>
            The chart shows the total value locked of users after each of their transactions. Each spot represents a
            transaction. The height of the spot corresponds with the total value locked after the transaction. When the
            spot is clicked, the detailed transaction information is shown. This chart allows users to track their
            transactions and their effect on the total value locked.
          </NoteTypography>
        ),
      }}
    >
      {role === "admin" && (
        <ButtonAddressTextField
          defaultValue={selectedAddress}
          events={{ onConfirm: (value) => setSelectedAddress(value) }}
        />
      )}
      <Paper style={{ marginTop: "1rem" }}>
        <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center">
          <DateGroupButton
            fullWidth={match}
            defaultActive={totalValueLocked?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchTotalValueLocked(pool, { from, to }, { active });
              },
            }}
            config={dateOption().map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchTotalValueLocked(pool, { from: data.from, to: data.to }, { active });
              },
            }))}
          />
        </Box>
        {check ? (
          <LineChart series={series} option={getOption(setChartId)} />
        ) : role === "admin" ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
          </Box>
        )}
      </Paper>
      <Box mt={2}>
        <EventTvlTable data={eventData} />
      </Box>
    </DetailMonitoringWrapper>
  );
}
